import type { EmptyObject } from '@/utils/primitives'
import type { Cell } from '@rundown-studio/types'

import { proxy } from 'valtio'
import { devtools } from 'valtio/utils'

/**
 * Modal registry
 */
type Modal =
  | {
      key: 'SHORTCUTS'
      props: EmptyObject
      returns: void
    }
  | {
      key: 'MENTIONS'
      props: EmptyObject
      returns: void
    }
  | {
      key: 'VARIABLES'
      props: EmptyObject
      returns: void
    }
  | {
      key: 'CELL_HISTORY'
      props: { cellId: Cell['id'] }
      returns: void
    }
  | {
      key: 'CELL_IMAGENODE'
      props: { imageUrl: string }
      returns: void
    }

type ModalConfig = {
  id: string
  payload: Modal
  /** Resolve function. Stored as any internally, but strictly typed at the boundary */
  resolve: (value: unknown) => void
  options?: { closeOnOverlayClick?: boolean }
}

interface ModalState {
  stack: ModalConfig[]
}

/**
 * Modal Store
 */
export const modalStore = proxy<ModalState>({
  stack: [],
})

/**
 * Modal Store actions
 */
export const modalActions = {
  /**
   * Open modal
   */
  open<K extends ModalKey>(
    key: K,
    props: ExtractModal<K>['props'],
    options?: ModalConfig['options'],
  ): Promise<ExtractModal<K>['returns']> {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).substring(2, 9)

      modalStore.stack.push({
        id,
        payload: { key, props } as Modal,
        resolve: (val: unknown) => {
          // Satisfy TS by casting the resolve
          const finalResolve = resolve as (value: unknown) => void
          finalResolve(val)
        },
        options,
      })
    })
  },

  /**
   * Close modal (with result)
   */
  close(id: string, result: unknown) {
    const modal = modalStore.stack.find((m) => m.id === id)
    if (modal) {
      modal.resolve(result)
      modalStore.stack = modalStore.stack.filter((m) => m.id !== id)
    }
  },

  /**
   * Close all open modal (ignore results)
   */
  closeAll() {
    modalStore.stack = []
  },

  /**
   * Close modal (ignore result)
   */
  dismiss(id: string) {
    const modal = modalStore.stack.find((m) => m.id === id)
    if (modal) {
      modal.resolve(null)
      modalStore.stack = modalStore.stack.filter((m) => m.id !== id)
    }
  },

  /**
   * Open only if not already open, otherwise close
   */
  toggle<K extends ModalKey>(
    key: K,
    props: ExtractModal<K>['props'],
    options?: ModalConfig['options'],
  ): void {
    const isOpen = modalStore.stack.find((m) => m.payload.key === key)

    if (isOpen) {
      this.close(isOpen.id, undefined)
    } else {
      this.open(key, props, options)
    }
  },
}

// #region ============== DEV

if (process.env.NODE_ENV !== 'production') {
  devtools(modalStore, { name: 'modalStore', enabled: true })
}

// #endregion

// #region ============== TYPE HELPERS

type ModalKey = Modal['key']

export type ExtractModal<K extends ModalKey> = Extract<Modal, { key: K }>

export type ModalResolverProps = {
  payload: Modal
  onResolve: (data: unknown) => void
}

export type ModalComponentProps<K extends ModalKey> = {
  props: ExtractModal<K>['props']
  onResolve: (data: ExtractModal<K>['returns']) => void
}

// #endregion
